import {useCallback,useEffect,useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import {ArrowForwardRounded} from "@mui/icons-material";
import {Link} from "react-router-dom";
import {useAppTheme} from "../../context/ThemeContext";

const TOURNAMENT_SLUG="nexus-cup-2026";
const ENDPOINT=`/standings/public/tournaments/${TOURNAMENT_SLUG}/`;

const getNumber=value=>{
  const number=Number(value);
  return Number.isFinite(number)?number:0;
};

const formatDifference=value=>{
  const number=getNumber(value);
  return number>0?`+${number}`:String(number);
};

const TABLE_COLUMNS={
  xs:"24px minmax(0,1fr) 28px 28px 32px 36px",
  sm:"28px minmax(0,1fr) 30px 30px 30px 30px 36px 42px"
};

function StandingTable({group,colors,shape}){
  const teams=Array.isArray(group?.teams)?group.teams:[];
  const advancing=getNumber(group?.teams_advance);

  const gridSx={
    display:"grid",
    gridTemplateColumns:TABLE_COLUMNS,
    columnGap:{xs:0.5,sm:0.75},
    alignItems:"center",
    px:{xs:1.25,sm:1.75}
  };

  const headerSx={
    fontSize:{xs:9,sm:10},
    fontWeight:600,
    color:colors.textSecondary,
    textAlign:"center",
    whiteSpace:"nowrap"
  };

  const numberSx={
    fontSize:{xs:11,sm:12,md:13},
    fontWeight:400,
    color:colors.text,
    textAlign:"center",
    fontVariantNumeric:"tabular-nums",
    lineHeight:1.3
  };

  const hiddenOnMobile={
    display:{xs:"none",sm:"block"}
  };

  return(
    <Box
      sx={{
        width:"100%",
        minWidth:0,
        overflow:"hidden",
        border:`1px solid ${colors.border}`,
        borderRadius:shape.radiusLarge,
        backgroundColor:colors.surface
      }}
    >
      <Box
        sx={{
          minHeight:58,
          px:{xs:2,sm:2.5},
          py:1.5,
          display:"flex",
          alignItems:"center",
          backgroundColor:colors.primary
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize:{xs:16,sm:17},
            fontWeight:600,
            color:"#FFFFFF",
            lineHeight:1.3
          }}
        >
          {group.name}
        </Typography>
      </Box>

      <Box
        sx={{
          ...gridSx,
          minHeight:45,
          backgroundColor:colors.backgroundSoft,
          borderBottom:`1px solid ${colors.border}`
        }}
      >
        <Typography sx={headerSx}>#</Typography>

        <Typography
          sx={{
            ...headerSx,
            textAlign:"left"
          }}
        >
          EQUIPO
        </Typography>

        <Typography sx={headerSx}>PJ</Typography>
        <Typography sx={headerSx}>G</Typography>

        <Typography
          sx={{
            ...headerSx,
            ...hiddenOnMobile
          }}
        >
          E
        </Typography>

        <Typography
          sx={{
            ...headerSx,
            ...hiddenOnMobile
          }}
        >
          P
        </Typography>

        <Typography sx={headerSx}>DG</Typography>
        <Typography
          sx={{
            ...headerSx,
            color:colors.primary
          }}
        >
          PTS
        </Typography>
      </Box>

      {teams.map((item,index)=>{
        const team=item.team||{};
        const qualified=index<advancing;

        return(
          <Box
            key={team.slug||index}
            sx={{
              ...gridSx,
              minHeight:{xs:64,sm:70},
              py:1.2,
              backgroundColor:qualified
                ?colors.backgroundSoft
                :colors.surface,
              borderBottom:index===teams.length-1
                ?"none"
                :`1px solid ${colors.border}`,
              borderLeft:qualified
                ?`3px solid ${colors.secondary}`
                :"3px solid transparent"
            }}
          >
            <Typography
              sx={{
                ...numberSx,
                fontWeight:qualified?600:400,
                color:qualified
                  ?colors.secondary
                  :colors.textSecondary
              }}
            >
              {item.position||index+1}
            </Typography>

            <Box
              sx={{
                display:"flex",
                alignItems:"center",
                gap:{xs:0.7,sm:1},
                minWidth:0
              }}
            >
              <Box
                sx={{
                  width:{xs:27,sm:33},
                  height:{xs:27,sm:33},
                  flexShrink:0,
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center"
                }}
              >
                {team.logo?(
                  <Box
                    component="img"
                    src={team.logo}
                    alt=""
                    loading="lazy"
                    sx={{
                      width:"100%",
                      height:"100%",
                      objectFit:"contain"
                    }}
                  />
                ):(
                  <Box
                    sx={{
                      width:27,
                      height:27,
                      display:"flex",
                      alignItems:"center",
                      justifyContent:"center",
                      borderRadius:"50%",
                      border:`1px solid ${colors.border}`,
                      backgroundColor:colors.backgroundSoft,
                      color:colors.secondary,
                      fontSize:12,
                      fontWeight:600
                    }}
                  >
                    {(team.name||"E").charAt(0)}
                  </Box>
                )}
              </Box>

              <Typography
                title={team.name||""}
                sx={{
                  minWidth:0,
                  fontSize:{xs:10.5,sm:11.5,md:12.5},
                  fontWeight:qualified?600:500,
                  color:colors.text,
                  lineHeight:1.35,
                  overflow:"hidden",
                  display:"-webkit-box",
                  WebkitLineClamp:2,
                  WebkitBoxOrient:"vertical",
                  overflowWrap:"anywhere"
                }}
              >
                {team.name||"Equipo"}
              </Typography>
            </Box>

            <Typography sx={numberSx}>
              {getNumber(item.played)}
            </Typography>

            <Typography sx={numberSx}>
              {getNumber(item.wins)}
            </Typography>

            <Typography
              sx={{
                ...numberSx,
                ...hiddenOnMobile
              }}
            >
              {getNumber(item.draws)}
            </Typography>

            <Typography
              sx={{
                ...numberSx,
                ...hiddenOnMobile
              }}
            >
              {getNumber(item.losses)}
            </Typography>

            <Typography sx={numberSx}>
              {formatDifference(item.goal_difference)}
            </Typography>

            <Typography
              sx={{
                ...numberSx,
                fontWeight:600,
                color:colors.primary
              }}
            >
              {getNumber(item.points)}
            </Typography>
          </Box>
        );
      })}

      {teams.length===0&&(
        <Box
          sx={{
            px:2,
            py:4,
            textAlign:"center"
          }}
        >
          <Typography
            sx={{
              fontSize:13,
              fontWeight:400,
              color:colors.textSecondary
            }}
          >
            Todavía no hay equipos en este grupo.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function StandingsSection(){
  const {colors,typography,shape}=useAppTheme();

  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [selectedIndex,setSelectedIndex]=useState(0);

  const loadData=useCallback(async signal=>{
    try{
      const response=await fetch(ENDPOINT,{
        method:"GET",
        signal,
        headers:{
          Accept:"application/json"
        }
      });

      if(!response.ok){
        throw new Error("No se pudieron consultar las posiciones.");
      }

      const json=await response.json();

      if(signal.aborted) return;

      setData(json);
      setError("");
    }catch(err){
      if(signal.aborted) return;

      setError("No fue posible cargar las tablas de posiciones.");
    }finally{
      if(!signal.aborted){
        setLoading(false);
      }
    }
  },[]);

  useEffect(()=>{
    const controller=new AbortController();

    loadData(controller.signal);

    return()=>{
      controller.abort();
    };
  },[loadData]);

  const categories=Array.isArray(data?.categories)
    ?data.categories
    :[];

  const selectedCategory=categories[selectedIndex]||categories[0];

  const groups=Array.isArray(selectedCategory?.groups)
    ?selectedCategory.groups
    :[];

  return(
    <Box
      component="section"
      aria-label="Tablas de posiciones"
      sx={{
        width:"100%",
        backgroundColor:colors.surface,
        py:{xs:6,sm:7,md:9}
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          px:{xs:2,sm:3,md:5,lg:7}
        }}
      >
        <Box
          sx={{
            maxWidth:760,
            mx:"auto",
            textAlign:"center"
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize:{xs:28,sm:34,md:42},
              fontWeight:600,
              lineHeight:1.15,
              letterSpacing:"-0.035em",
              color:colors.primary,
              textAlign:"center"
            }}
          >
            Tablas de posiciones
          </Typography>

          <Typography
            sx={{
              mt:1.5,
              fontSize:{xs:14,sm:15,md:16},
              fontWeight:400,
              lineHeight:1.8,
              color:colors.textSecondary,
              textAlign:"center"
            }}
          >
            Sigue la clasificación de cada grupo, compara
            los resultados y descubre qué equipos avanzan
            a la siguiente fase.
          </Typography>
        </Box>

        {loading?(
          <Box
            sx={{
              minHeight:280,
              display:"flex",
              alignItems:"center",
              justifyContent:"center"
            }}
          >
            <CircularProgress sx={{color:colors.secondary}}/>
          </Box>
        ):error?(
          <Box
            sx={{
              maxWidth:640,
              mx:"auto",
              mt:5,
              px:3,
              py:4,
              textAlign:"center",
              border:`1px solid ${colors.border}`,
              borderRadius:shape.radiusLarge
            }}
          >
            <Typography
              sx={{
                fontSize:14,
                fontWeight:400,
                color:colors.textSecondary,
                textAlign:"center"
              }}
            >
              {error}
            </Typography>
          </Box>
        ):categories.length===0?(
          <Box
            sx={{
              maxWidth:640,
              mx:"auto",
              mt:5,
              px:3,
              py:4,
              textAlign:"center",
              border:`1px solid ${colors.border}`,
              borderRadius:shape.radiusLarge
            }}
          >
            <Typography
              sx={{
                fontSize:14,
                fontWeight:400,
                color:colors.textSecondary,
                textAlign:"center"
              }}
            >
              No hay tablas de posiciones disponibles.
            </Typography>
          </Box>
        ):(
          <>
            <Box
              sx={{
                mt:{xs:4,md:5},
                mb:3,
                display:"flex",
                alignItems:"center",
                justifyContent:{
                  xs:"center",
                  md:"space-between"
                },
                gap:2,
                flexWrap:"wrap"
              }}
            >
              <Box
                sx={{
                  textAlign:{xs:"center",md:"left"}
                }}
              >
                <Typography
                  sx={{
                    fontSize:{xs:16,sm:18},
                    fontWeight:600,
                    color:colors.primary
                  }}
                >
                  {data?.tournament?.name}
                  {data?.tournament?.season
                    ?` ${data.tournament.season}`
                    :""}
                </Typography>

                <Typography
                  sx={{
                    mt:0.4,
                    fontSize:12,
                    fontWeight:400,
                    color:colors.textSecondary
                  }}
                >
                  {selectedCategory?.phase?.name||"Fase de grupos"}
                </Typography>
              </Box>

              {categories.length>1&&(
                <Select
                  size="small"
                  value={Math.min(selectedIndex,categories.length-1)}
                  onChange={event=>setSelectedIndex(Number(event.target.value))}
                  sx={{
                    minWidth:190,
                    height:42,
                    borderRadius:shape.radius,
                    color:colors.text,
                    fontSize:13,
                    fontWeight:500,
                    backgroundColor:colors.surface,
                    "& .MuiOutlinedInput-notchedOutline":{
                      borderColor:colors.border
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline":{
                      borderColor:colors.secondary
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":{
                      borderColor:colors.secondary
                    }
                  }}
                >
                  {categories.map((item,index)=>(
                    <MenuItem
                      key={`${item.category?.slug}-${item.phase?.slug}-${index}`}
                      value={index}
                    >
                      {item.category?.name} · {item.phase?.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>

            <Box
              sx={{
                display:"grid",
                gridTemplateColumns:{
                  xs:"minmax(0,1fr)",
                  lg:"repeat(2,minmax(0,1fr))"
                },
                gap:{xs:2,md:3},
                alignItems:"start"
              }}
            >
              {groups.map(group=>(
                <StandingTable
                  key={group.slug}
                  group={group}
                  colors={colors}
                  shape={shape}
                />
              ))}
            </Box>

            {groups.length===0&&(
              <Box
                sx={{
                  py:4,
                  textAlign:"center",
                  border:`1px solid ${colors.border}`,
                  borderRadius:shape.radiusLarge
                }}
              >
                <Typography
                  sx={{
                    fontSize:14,
                    color:colors.textSecondary
                  }}
                >
                  Esta fase todavía no tiene grupos registrados.
                </Typography>
              </Box>
            )}
          </>
        )}

      </Container>
    </Box>
  );
}